/* eslint-disable unicorn/prevent-abbreviations */
import { Ikon } from '@studio/design-system/button';
import { Arrow } from '@studio/design-system/tooltip';
import * as RTooltip from '@radix-ui/react-tooltip';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import { ItemIris } from './item-iris.jsx';
import { Logo } from '../../logo.jsx';
import type { GroupProps, StudioDescriptor } from '../../types.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { motion } from '@studio/design-system/motion';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';

/** Iris vendor `<Group>` component with new UI features. */
export const GroupIris = ({
  className,
  handler,
  organization,
  rooms,
  selection,
  ...props
}: GroupProps): ReactElement => {
  const passthru = 'pl-16';

  const { enabled: isOrganizationSettingsEnabled, loading } = useFeatureFlag(
    PostHogFeatureFlag.ORGANIZATIONS_FEATURE
  );

  const renderOrgIcon = !loading && isOrganizationSettingsEnabled;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const tooltipContent = (
    <div className='max-w-[326px] rounded-8 bg-[#10131d] p-8'>
      <h3 className='leading-tight mb-4 text-sm font-demibold text-[#FFFFFF]'>
        Creating a new studio
      </h3>
      <p className='text-sm font-medium leading-[18px] text-[#b4b9c8]'>
        Download Iris on a new computer and sign in with your account. Iris will handle the rest.
      </p>
    </div>
  );

  return (
    <div className={twMerge(className)} {...props}>
      {/* Account Heading */}
      {!!handler.onSelectRoom && (
        <div className='group mb-6 flex h-34 items-center justify-between gap-8 px-16' {...props}>
          <div className='flex items-center gap-8'>
            <Logo
              className='h-[21px] w-[21px]'
              name={organization.name}
              photo={organization.photoUrl}
            />
            <span className='text-md font-regular text-light'>Studios</span>
          </div>

          <RTooltip.Provider>
            <RTooltip.Root open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
              <RTooltip.Trigger asChild>
                <Ikon
                  {...STagNew(Sel.SettingsSwitcher, 'create_studio')}
                  aria-label='Create Studio'
                  className='cursor-pointer text-md font-demibold'
                  icon='plus'
                  onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                />
              </RTooltip.Trigger>
              <RTooltip.Portal>
                <RTooltip.Content
                  {...DataSTag(Sel.SettingsSwitcher, 'create_studio_guidance')}
                  side='right'
                  sideOffset={12}
                  style={{ zIndex: 100 }}
                >
                  {/* Radix clones children into a hidden accessibility node, so tag Content once. */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                  >
                    {tooltipContent}
                  </motion.div>
                  <Arrow className='fill-[#10131d]' />
                </RTooltip.Content>
              </RTooltip.Portal>
            </RTooltip.Root>
          </RTooltip.Provider>

          {/* Icon action that opens the organization settings */}
          {!!handler.onShowOrganizationSettings && renderOrgIcon && (
            <Ikon
              className='opacity-0 transition hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100'
              icon='settings'
              onClick={(): void => {
                handler.onShowOrganizationSettings?.(organization.id);
              }}
            />
          )}
        </div>
      )}

      <div className='w-full border-b border-secondary/10 pb-6' />

      {/* Rooms */}
      {!!handler.onSelectRoom &&
        (rooms as StudioDescriptor[]).map((descriptor) => {
          const { room } = descriptor;
          const isSelected = room.id === selection?.roomId;

          return (
            <ItemIris
              key={room.id}
              badges={descriptor.badges}
              className={twMerge(
                isSelected
                  ? 'cursor-default text-[#FFFFFF] !opacity-100'
                  : `${passthru} text-[#9DA8BE]`,
                'group flex items-center justify-items-center align-middle text-md font-demibold',
                'h-40 py-[11px] pl-16 text-md font-regular leading-130',
                'data-highlighted:bg-transparent data-highlighted:text-white'
              )}
              disabled={isSelected}
              icon={
                isSelected ? (
                  <Ikon
                    className='text-[#B8C5D6] hover:bg-transparent hover:text-[#B8C5D6]'
                    icon='check'
                  />
                ) : undefined
              }
              label={room.name}
              status={descriptor.status}
              onSelect={(): void => {
                handler.onSelectRoom?.(room.id);
                handler.onSelectOrganization?.(room.organizationId);
              }}
            />
          );
        })}
    </div>
  );
};
